import * as React from "react";
import {
  Flex,
  Text,
  Heading,
  Box,
  Link,
  Code,
  Blockquote,
  Strong,
  Em,
} from "@radix-ui/themes";
import type { TextProps, HeadingProps } from "@radix-ui/themes";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { v0_9 } from "@a2ui-sdk/react";
import type { StandardCatalog } from "@a2ui-sdk/types/0.9";
import { useStringBinding } from "../hooks/useA2UIStringBinding";

type A2UITextVariant = NonNullable<StandardCatalog.TextComponentProps["variant"]>;

type VariantConfig = { type: "heading"; props: HeadingProps } | { type: "text"; props: TextProps };
const variantPropsMap: Record<A2UITextVariant, VariantConfig> = {
  h1: { type: "heading", props: { as: "h1", size: "5", weight: "bold" } },
  h2: { type: "heading", props: { as: "h2", size: "4", weight: "bold" } },
  h3: { type: "heading", props: { as: "h3", size: "3", weight: "bold" } },
  h4: { type: "heading", props: { as: "h4", size: "2", weight: "bold" } },
  h5: { type: "heading", props: { as: "h5", size: "2", weight: "medium" } },
  caption: { type: "text", props: { as: "div", size: "1", color: "gray" } },
  body: { type: "text", props: { as: "div", size: "2" } },
};

export interface MarkdownTextProps {
  surfaceId: string;
  text: string;
  variant?: A2UITextVariant;
  weight?: number;
}

export const sharedMarkdownComponents: Components = {
  h1: ({ children }) => <Heading as="h1" size="5" mb="2">{children}</Heading>,
  h2: ({ children }) => <Heading as="h2" size="4" mb="2">{children}</Heading>,
  h3: ({ children }) => <Heading as="h3" size="3" mb="2">{children}</Heading>,
  h4: ({ children }) => <Heading as="h4" size="2" mb="2" weight="bold">{children}</Heading>,
  h5: ({ children }) => <Heading as="h5" size="2" mb="2" weight="medium">{children}</Heading>,
  h6: ({ children }) => <Heading as="h6" size="1" mb="2" weight="bold">{children}</Heading>,
  p: ({ children }) => <Text as="p" size="2" mb="2" style={{ lineHeight: 1.5 }}>{children}</Text>,
  a: ({ href, children }) => <Link href={href} target="_blank" rel="noopener noreferrer">{children}</Link>,
  code: ({ className, children }: React.ComponentPropsWithoutRef<'code'>) => {
    const isInline = !className;
    return (
      <Code
        variant="soft"
        style={{
          whiteSpace: isInline ? "normal" : "pre-wrap",
          wordBreak: isInline ? "break-word" : "normal",
        }}
        className={className}
      >
        {children}
      </Code>
    );
  },
  pre: ({ children }: React.HTMLAttributes<HTMLPreElement>) => {
    return (
      <Box
        mb="2"
        style={{
          overflowX: "auto",
          backgroundColor: "var(--gray-3)",
          borderRadius: "var(--radius-3)",
          padding: "8px",
        }}
      >
        <pre style={{ margin: 0 }}>{children}</pre>
      </Box>
    );
  },
  blockquote: ({ children }) => <Blockquote size="2" mb="2">{children}</Blockquote>,
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Em>{children}</Em>,
  ul: ({ children }) => (
    <Flex
      asChild
      direction="column"
      gap="1"
      mb="2"
      style={{ paddingLeft: "20px", listStyleType: "disc", margin: 0 }}
    >
      <ul>{children}</ul>
    </Flex>
  ),
  ol: ({ children }) => (
    <Flex
      asChild
      direction="column"
      gap="1"
      mb="2"
      style={{ paddingLeft: "20px", listStyleType: "decimal", margin: 0 }}
    >
      <ol>{children}</ol>
    </Flex>
  ),
  li: ({ children }) => (
    <Text asChild size="2" style={{ lineHeight: 1.5 }}>
      <li>{children}</li>
    </Text>
  ),
};

export const formatMarkdownText = (text: string) => {
  if (!text) return text;
  let formatted = text.replace(/(^|\n)[ \t]*[•●]\s+/g, "$1- ");
  formatted = formatted.replace(/([^\n])\n([ \t]*- )/g, "$1\n\n$2");
  return formatted;
};

export const MarkdownTextComponent: React.FC<MarkdownTextProps> = ({
  surfaceId,
  text,
  variant = "body",
  weight,
}) => {
  const resolvedText = useStringBinding(surfaceId, text, "");

  if (!resolvedText) return null;

  const config = variantPropsMap[variant] || variantPropsMap.body;
  const formattedText = formatMarkdownText(resolvedText);

  const MarkdownContent = (
    <ReactMarkdown components={sharedMarkdownComponents} remarkPlugins={[remarkGfm]}>
      {formattedText}
    </ReactMarkdown>
  );

  return (
    <Box style={weight ? { flexGrow: weight } : undefined} className="markdown-content">
      {config.type === "heading" ? (
        <Heading {...config.props}>{MarkdownContent}</Heading>
      ) : (
        <Text {...config.props}>{MarkdownContent}</Text>
      )}
    </Box>
  );
};
